import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Table, Tag, Badge, Space, Typography, Switch, message } from 'antd';
import {
  EyeOutlined,
  StopOutlined,
  SafetyOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { getLogsStats, getLogs, getSettings, updateSettings } from '../services/api';

const { Text } = Typography;

function DashboardPage() {
  const [stats, setStats] = useState({
    today_total: 0,
    today_blocked: 0,
    blacklist_count: 0,
    trend: []
  });
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockEmptyWebrtc, setBlockEmptyWebrtc] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadSettings();
    const timer = setInterval(loadData, 30000);
    return () => clearInterval(timer);
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getSettings();
      setBlockEmptyWebrtc(res.data.block_empty_webrtc);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleWebrtc = async (checked) => {
    setSettingsLoading(true);
    try {
      await updateSettings({ block_empty_webrtc: checked });
      setBlockEmptyWebrtc(checked);
      message.success(checked ? '已开启：WebRTC为空时拦截' : '已关闭：WebRTC为空时拦截');
    } catch (err) {
      message.error('设置更新失败');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        getLogsStats(),
        getLogs({ page: 1, page_size: 8 })
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data.items);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const passRate = stats.today_total > 0
    ? ((stats.today_total - stats.today_blocked) / stats.today_total * 100).toFixed(1)
    : '100.0';

  const columns = [
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 130,
      render: (val) => <Text code style={{ fontSize: 12 }}>{val}</Text>
    },
    {
      title: '文件',
      dataIndex: 'file_id',
      key: 'file_id',
      width: 100,
      render: (val) => <Tag>{val || '-'}</Tag>
    },
    {
      title: '状态',
      dataIndex: 'is_blocked',
      key: 'is_blocked',
      width: 80,
      render: (val) => val
        ? <Badge status="error" text={<Text type="danger" style={{ fontSize: 12 }}>拦截</Text>} />
        : <Badge status="success" text={<Text type="success" style={{ fontSize: 12 }}>放行</Text>} />
    },
    {
      title: '原因',
      dataIndex: 'block_reason',
      key: 'block_reason',
      width: 120,
      render: (val) => val ? <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text> : '-'
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (val) => <Text type="secondary" style={{ fontSize: 11 }}>{val}</Text>
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>今日请求</span>}
              value={stats.today_total}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#fff', fontSize: 28 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: 'linear-gradient(135deg, #f5576c 0%, #ff6b6b 100%)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>已拦截</span>}
              value={stats.today_blocked}
              prefix={<StopOutlined />}
              valueStyle={{ color: '#fff', fontSize: 28 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>放行率</span>}
              value={passRate}
              suffix="%"
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#fff', fontSize: 28 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>黑名单规则</span>}
              value={stats.blacklist_count}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#fff', fontSize: 28 }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            size="small"
            title={<Space><ClockCircleOutlined /> 最近访问</Space>}
            bordered={false}
            style={{ borderRadius: 8 }}
          >
            <Table
              columns={columns}
              dataSource={recentLogs}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            size="small"
            title="近7日趋势"
            bordered={false}
            style={{ borderRadius: 8, marginBottom: 16 }}
          >
            {stats.trend && stats.trend.length > 0 ? (
              <div style={{ fontSize: 12 }}>
                {stats.trend.map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: idx < stats.trend.length - 1 ? '1px solid #f5f5f5' : 'none'
                  }}>
                    <Text type="secondary">{item.date}</Text>
                    <Space size={16}>
                      <Text>{item.total} 次</Text>
                      <Text type="danger">{item.blocked} 拦截</Text>
                    </Space>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary">暂无数据</Text>
            )}
          </Card>
          <Card
            size="small"
            title={<Space><SettingOutlined /> 安全设置</Space>}
            bordered={false}
            style={{ borderRadius: 8 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <div>
                <Text strong style={{ fontSize: 13 }}>WebRTC 为空时拦截</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>开启后，无法获取 WebRTC IP 的请求将被拦截</Text>
              </div>
              <Switch
                checked={blockEmptyWebrtc}
                onChange={handleToggleWebrtc}
                loading={settingsLoading}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default DashboardPage;
